/* Copyright 2021, Milkdown by Mirone. */
import { css } from '@emotion/css';
import { createCmd, createCmdKey, themeToolCtx } from '@milkdown/core';
import { createNode, findSelectedNodeOfType } from '@milkdown/utils';
import { InputRule } from 'prosemirror-inputrules';
export const ModifyImage = createCmdKey();
export const InsertImage = createCmdKey();
const id = 'image';
export const image = createNode((options, utils) => {
    var _a, _b;
    const placeholder = Object.assign({ loading: 'Loading...', empty: 'Add an Image', failed: 'Image loads failed' }, ((_a = options === null || options === void 0 ? void 0 : options.placeholder) !== null && _a !== void 0 ? _a : {}));
    const isBlock = (_b = options === null || options === void 0 ? void 0 : options.isBlock) !== null && _b !== void 0 ? _b : false;
    const containerStyle = utils.getStyle((themeTool) => css `
                display: inline-block;
                position: relative;
                text-align: center;
                font-size: 0;
                vertical-align: text-bottom;
                ${isBlock
        ? `
                width: 100%;
                margin: 0 auto;
                `
        : ''}
                img {
                    max-width: 100%;
                    height: auto;
                    object-fit: contain;
                    margin: 0 2px;
                }
                .icon,
                .placeholder {
                    display: none;
                }

                &.system {
                    width: 100%;
                    padding: 0 2rem;

                    img {
                        width: 0;
                        height: 0;
                        display: none;
                    }

                    .icon,
                    .placeholder {
                        display: inline;
                    }

                    box-sizing: border-box;
                    height: 3rem;
                    background-color: ${themeTool.palette('background')};
                    border-radius: ${themeTool.size.radius};
                    display: inline-flex;
                    gap: 2rem;
                    justify-content: flex-start;
                    align-items: center;
                    .placeholder {
                        margin: 0;
                        line-height: 1;
                        &::before {
                            content: '';
                            font-size: 0.875rem;
                            color: ${themeTool.palette('neutral', 0.6)};
                        }
                    }
                }

                &.loading {
                    .placeholder {
                        &::before {
                            content: '${placeholder.loading}';
                        }
                    }
                }

                &.empty {
                    .placeholder {
                        &::before {
                            content: '${placeholder.empty}';
                        }
                    }
                }

                &.failed {
                    .placeholder {
                        &::before {
                            content: '${placeholder.failed}';
                        }
                    }
                }
            `);
    const style = utils.getStyle(() => css `
                display: inline-block;
                margin: 0 auto;
                object-fit: contain;
                width: 100%;
                position: relative;
                height: auto;
                text-align: center;
            `);
    return {
        id,
        schema: {
            inline: true,
            group: 'inline',
            draggable: true,
            marks: '',
            atom: true,
            defining: true,
            isolating: true,
            attrs: {
                src: { default: '' },
                alt: { default: null },
                title: { default: null },
                failed: { default: false },
                loading: { default: true },
                width: { default: 0 },
            },
            parseDOM: [
                {
                    tag: 'img[src]',
                    getAttrs: (dom) => {
                        if (!(dom instanceof HTMLElement)) {
                            throw new Error();
                        }
                        return {
                            failed: dom.classList.contains('failed'),
                            loading: dom.classList.contains('loading'),
                            src: dom.getAttribute('src') || '',
                            alt: dom.getAttribute('alt'),
                            title: dom.getAttribute('title') || dom.getAttribute('alt'),
                            width: dom.getAttribute('width') || 0,
                        };
                    },
                },
            ],
            toDOM: (node) => {
                return [
                    'img',
                    Object.assign(Object.assign({}, node.attrs), { class: utils.getClassName(node.attrs, id, node.attrs.failed ? 'failed' : '', node.attrs.loading ? 'loading' : '', style) }),
                ];
            },
        },
        parser: {
            match: ({ type }) => type === id,
            runner: (state, node, type) => {
                const url = node.url;
                const alt = node.alt;
                const title = node.title;
                state.addNode(type, {
                    src: url,
                    alt,
                    title,
                });
            },
        },
        serializer: {
            match: (node) => node.type.name === id,
            runner: (state, node) => {
                state.addNode('image', undefined, undefined, {
                    title: node.attrs.title,
                    url: node.attrs.src,
                    alt: node.attrs.alt,
                });
            },
        },
        commands: (nodeType) => [
            createCmd(InsertImage, (src = '') => (state, dispatch) => {
                if (!dispatch)
                    return true;
                const { tr } = state;
                const node = nodeType.create({ src });
                if (!node) {
                    return true;
                }
                const _tr = tr.replaceSelectionWith(node);
                dispatch(_tr.scrollIntoView());
                return true;
            }),
            createCmd(ModifyImage, (src = '') => (state, dispatch) => {
                const node = findSelectedNodeOfType(state.selection, nodeType);
                if (!node)
                    return false;
                const { tr } = state;
                dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.setNodeMarkup(node.pos, undefined, Object.assign(Object.assign({}, node.node.attrs), { loading: true, src })).scrollIntoView());
                return true;
            }),
        ],
        inputRules: (nodeType) => [
            new InputRule(/!\[(?<alt>.*?)]\((?<filename>.*?)\s*(?="|\))"?(?<title>[^"]+)?"?\)/, (state, match, start, end) => {
                const [okay, alt, src = '', title] = match;
                const { tr } = state;
                if (okay) {
                    tr.replaceWith(start, end, nodeType.create({ src, alt, title }));
                }
                return tr;
            }),
        ],
        view: (_editor, nodeType, node, view, getPos) => {
            const createIcon = utils.ctx.get(themeToolCtx).slots.icon;
            const container = document.createElement('span');
            container.className = utils.getClassName(node.attrs, id, containerStyle);
            container.contentEditable = 'false';
            const content = document.createElement('img');
            content.contentEditable = 'true';
            container.append(content);
            let icon = createIcon('image');
            const placeholder = document.createElement('span');
            placeholder.classList.add('placeholder');
            container.append(icon, placeholder);
            const setIcon = (name) => {
                const nextIcon = createIcon(name);
                container.replaceChild(nextIcon, icon);
                icon = nextIcon;
            };
            const loadImage = (src) => {
                container.classList.add('system', 'loading');
                setIcon('loading');
                const img = document.createElement('img');
                img.src = src;
                img.onerror = () => {
                    const { tr } = view.state;
                    const _tr = tr.setNodeMarkup(getPos(), nodeType, Object.assign(Object.assign({}, node.attrs), { src, loading: false, failed: true }));
                    view.dispatch(_tr);
                };
                img.onload = () => {
                    const { tr } = view.state;
                    const _tr = tr.setNodeMarkup(getPos(), nodeType, Object.assign(Object.assign({}, node.attrs), { width: img.width, src, loading: false, failed: false }));
                    view.dispatch(_tr);
                };
            };
            const { src, loading, title, alt, width } = node.attrs;
            content.src = src;
            content.title = title || alt;
            content.alt = alt;
            content.width = width;
            if (src.length === 0) {
                container.classList.add('system', 'empty');
                setIcon('image');
            }
            else if (loading) {
                loadImage(src);
            }
            return {
                dom: container,
                update: (updatedNode) => {
                    if (updatedNode.type.name !== id)
                        return false;
                    const { src, alt, title, loading, failed, width } = updatedNode.attrs;
                    content.src = src;
                    content.alt = alt;
                    content.title = title || alt;
                    content.width = width;
                    if (loading) {
                        loadImage(src);
                        return true;
                    }
                    if (failed) {
                        container.classList.remove('loading', 'empty');
                        container.classList.add('system', 'failed');
                        setIcon('brokenImage');
                        return true;
                    }
                    if (src.length > 0) {
                        container.classList.remove('system', 'empty', 'loading');
                        return true;
                    }
                    container.classList.add('system', 'empty');
                    setIcon('image');
                    return true;
                },
            };
        },
    };
});
//# sourceMappingURL=image.js.map